<template>
	<section>
		<el-tabs v-model="activeName" type="card">
			<!-- 认领商品 -->
			<el-tab-pane label="Claim Items" name="claim">
				<!-- 当前神医信息显示（仅护士） -->
				<el-alert 
					v-if="currentDoctorName && isNurse"
					:title="`Currently claiming for: ${currentDoctorName}`"
					type="info" 
					:closable="false" 
					show-icon
					style="margin-bottom: 15px;">
				</el-alert>
				
				<el-col :span="24" class="toolbar" style="padding-bottom: 0px;">
					<el-form :model="claimForm" :rules="claimRules" ref="claimForm" label-width="120px">
						<!-- 选择对应的Inbound记录 (仅护士) -->
						<el-row v-if="isNurse" :gutter="20" class="form-row-spaced">
							<el-col :span="24">
								<el-form-item label="Available Inbound" prop="inboundNo">
									<el-select
										v-model="claimForm.inboundNo"
										placeholder="Filter Inbound by typing 4+ UPC characters, or select Inbound to batch import."
										style="width: 100%;"
										clearable
										filterable
										remote
										:remote-method="searchAvailableInbounds"
										:loading="availableInboundsLoading"
										@change="onInboundChange">
										<el-option
											v-for="inbound in availableInbounds"
											:key="inbound.inboundNo"
											:label="`${inbound.productName} - Qty: ${inbound.qty}, Left: ${inbound.leftNum} (${inbound.warehouse})`"
											:value="inbound.inboundNo">
											<span style="float: left">{{inbound.productName}}</span>
											<span style="float: right; color: #8492a6; font-size: 13px">
												Left: {{inbound.leftNum}}/{{inbound.qty}}
											</span>
										</el-option>
										<el-option v-if="availableInbounds.length === 0" disabled value="">
											No incomplete inbound records available
										</el-option>
									</el-select>
								</el-form-item>
							</el-col>
						</el-row>
						
						<!-- 显示选中Inbound的详细信息 (仅护士) -->
						<el-row v-if="isNurse && selectedInboundInfo" :gutter="20" class="form-row-spaced">
							<el-col :span="24">
								<!-- 使用增强的状态显示组件 -->
								<InboundStatusCard 
									:inbound="selectedInboundInfo" 
									:show-actions="false"
									@status-refreshed="onInboundStatusRefreshed" />
							</el-col>
						</el-row>
						
						<el-row :gutter="20" class="form-row-spaced">
							<el-col :span="8">
								<el-form-item label="Tracking #" prop="tracking">
									<el-input 
										v-model="claimForm.tracking" 
										placeholder="Enter tracking number" 
										clearable
										@blur="searchTrackingInfo">
									</el-input>
								</el-form-item>
							</el-col>
							<el-col :span="8">
								<el-form-item label="UPC" prop="upc">
									<el-input 
										v-model="claimForm.upc" 
										:placeholder="getUpcPlaceholder()"
										:readonly="!!(isNurse && selectedInboundInfo && selectedInboundInfo.upc)"
										:class="{ 'readonly-input': isNurse && selectedInboundInfo && selectedInboundInfo.upc }"
										clearable
										@blur="validateUpcMatch">
										<template slot="suffix" v-if="isNurse && selectedInboundInfo && selectedInboundInfo.upc">
											<i class="el-icon-lock" style="color: #909399;"></i>
										</template>
									</el-input>
									<!-- 只读状态提示 -->
									<div v-if="isNurse && selectedInboundInfo && selectedInboundInfo.upc" class="readonly-hint">
										<i class="el-icon-info"></i> Automatically filled from selected Inbound record
									</div>
								</el-form-item>
							</el-col>
							<el-col :span="8">
								<el-form-item label="Quantity" prop="quantity">
									<el-input-number 
										v-model="claimForm.quantity" 
										:min="1" 
										:max="getMaxClaimableQty()"
										placeholder="Quantity"
										style="width: 100%;">
									</el-input-number>
								</el-form-item>
							</el-col>
						</el-row>
						<el-row :gutter="20">
							<el-col :span="12">
								<el-form-item label="Warehouse" prop="warehouseNo">
									<el-select 
										v-model="claimForm.warehouseNo" 
										:placeholder="getWarehousePlaceholder()"
										:disabled="!!(isNurse && selectedInboundInfo && selectedInboundInfo.warehouseNo)"
										:class="{ 'readonly-select': isNurse && selectedInboundInfo && selectedInboundInfo.warehouseNo }"
										style="width: 100%;">
										<el-option-group v-for="group in warehouses" :key="group.label" :label="group.label">
											<el-option 
												v-for="(item, index) in group.options" 
												:key="index" 
												:label="item.state + ' ' + item.city + ' ' + item.address" 
												:value="item.warehouseNo">
											</el-option>
										</el-option-group>
									</el-select>
									<!-- 只读状态提示 -->
									<div v-if="isNurse && selectedInboundInfo && selectedInboundInfo.warehouseNo" class="readonly-hint">
										<i class="el-icon-info"></i> Automatically selected from Inbound record
									</div>
								</el-form-item>
							</el-col>
							<el-col :span="12">
								<el-form-item>
									<el-button type="success" @click="addToClaimList" :loading="addToListLoading">Add to Claim List</el-button>
									<el-button type="warning" @click="showBatchImportDialog" :disabled="!claimForm.inboundNo || !isNurse" v-if="isNurse" :loading="batchImportLoading">Batch Upload</el-button>
									<el-button type="warning" @click="showDoctorBatchImportDialog" :disabled="!claimForm.warehouseNo || isNurse" v-if="!isNurse" :loading="doctorBatchImportLoading">Batch Upload</el-button>
									<el-button type="primary" @click="submitSelectedClaims" :loading="claimLoading" :disabled="selectedClaims.length === 0">Submit Selected Claims ({{selectedClaims.length}})</el-button>
								</el-form-item>
							</el-col>
						</el-row>
					</el-form>
				</el-col>

				<!-- 提示信息 -->
				<el-alert
					title="Claim Instructions"
					:description="'Enter tracking number, UPC, quantity, and select warehouse. Add items to claim list, then submit all claims together.'"
					type="info"
					show-icon
					:closable="false"
					style="margin-bottom: 20px;">
				</el-alert>

				<!-- 待提交认领列表 -->
				<el-card v-if="claimList.length > 0" style="margin-bottom: 20px;">
					<div slot="header">
						<span>Claim List ({{claimList.length}} items, {{selectedClaims.length}} selected)</span>
						<div style="float: right;">
							<el-button size="small" type="primary" @click="selectAllClaims" :disabled="claimList.length === 0">Select All</el-button>
							<el-button size="small" type="warning" @click="clearSelection" :disabled="selectedClaims.length === 0">Clear Selection</el-button>
							<el-button size="small" type="danger" @click="clearClaimList">Clear All</el-button>
						</div>
					</div>
					<el-table :data="claimList" border style="width: 100%;" @selection-change="handleSelectionChange" ref="claimTable">
						<el-table-column type="selection" width="55"></el-table-column>
						<!-- Inbound列仅护士显示 -->
						<el-table-column v-if="isNurse" prop="inboundInfo" label="Inbound" width="300px">
							<template slot-scope="scope">
								<div>{{scope.row.productName}}</div>
								<small class="text-muted">Inbound #{{scope.row.inboundNo}}</small>
							</template>
						</el-table-column>
						<!-- 神医显示UPC信息列 -->
						<el-table-column v-if="!isNurse" prop="upc" label="UPC" width="220px">
							<template slot-scope="scope">
								<div>{{scope.row.upc}}</div>
								<small class="text-muted">Mixed Package Item</small>
							</template>
						</el-table-column>
						<el-table-column prop="tracking" label="Tracking #" width="320px"></el-table-column>
						<el-table-column prop="quantity" label="Quantity" width="100px" align="center"></el-table-column>
						<el-table-column prop="warehouseName" label="Warehouse" min-width="200px"></el-table-column>
						<el-table-column label="Actions" width="100px" align="center">
							<template slot-scope="scope">
								<el-button size="mini" type="danger" @click="removeFromClaimList(scope.$index)">Remove</el-button>
							</template>
						</el-table-column>
					</el-table>
				</el-card>
			</el-tab-pane>

			<!-- 我的认领记录 -->
			<el-tab-pane label="My Claims" name="myClaims">
				<el-col :span="24" class="toolbar" style="padding-bottom: 0px;">
					<el-form :inline="true" :model="myClaimFilters">
						<el-form-item>
							<el-input 
								v-model="myClaimFilters.tracking" 
								placeholder="Tracking Number" 
								clearable
								@keyup.enter.native="getMyClaimList">
								<el-button slot="append" icon="el-icon-search" @click="getMyClaimList"></el-button>
							</el-input>
						</el-form-item>
						<el-form-item>
							<el-input 
								v-model="myClaimFilters.upc" 
								placeholder="UPC" 
								clearable
								@keyup.enter.native="getMyClaimList">
							</el-input>
						</el-form-item>
						<el-form-item>
							<el-select v-model="myClaimFilters.claimStatus" placeholder="Status" clearable>
								<el-option label="Pending Review" value="0"></el-option>
								<el-option label="Approved" value="1"></el-option>
								<el-option label="Distributed" value="2"></el-option>
								<el-option label="Rejected" value="3"></el-option>
								<el-option label="Quantity Adjusted" value="4"></el-option>
							</el-select>
						</el-form-item>
						<el-form-item>
							<el-button type="primary" @click="getMyClaimList">Search</el-button>
						</el-form-item>
					</el-form>
				</el-col>

				<!-- 认领记录列表 -->
				<el-table 
					stripe 
					:data="myClaimList" 
					highlight-current-row 
					v-loading="myClaimListLoading" 
					style="width: 100%;">
					<el-table-column prop="tracking" label="Tracking #" width="320px">
					</el-table-column>
					<el-table-column prop="upc" label="UPC" width="250px">
					</el-table-column>
					<el-table-column label="Quantity Info" width="160px">
						<template slot-scope="scope">
							<p>Original: <strong>{{scope.row.originalClaimedQty || scope.row.claimedQty}}</strong></p>
							<p v-if="scope.row.originalClaimedQty && scope.row.originalClaimedQty !== scope.row.claimedQty" class="note">
								Final: <strong>{{scope.row.claimedQty}}</strong>
							</p>
						</template>
					</el-table-column>
					<el-table-column prop="claimDate" label="Claim Date" width="160px">
						<template slot-scope="scope">
							{{formatDate(scope.row.claimDate)}}
						</template>
					</el-table-column>
					<el-table-column label="Status" width="120px">
						<template slot-scope="scope">
							<el-tag 
								:type="getClaimStatusTagType(scope.row.claimStatus)" 
								size="small">
								{{getClaimStatusText(scope.row.claimStatus)}}
							</el-tag>
						</template>
					</el-table-column>
					<el-table-column prop="reviewReason" label="Review Note" min-width="200px">
						<template slot-scope="scope">
							<p v-if="scope.row.reviewReason">{{scope.row.reviewReason}}</p>
							<span v-else class="note">—</span>
						</template>
					</el-table-column>
					<el-table-column label="Actions" min-width="140" align="center">
						<template slot-scope="scope">
							<div class="action-buttons">
								<el-button 
									size="mini" 
									type="danger" 
									v-if="scope.row.claimStatus === 0"
									class="action-btn"
									@click="cancelClaim(scope.row)">
									Cancel
								</el-button>
								<el-button 
									size="mini" 
									type="primary"
									class="action-btn"
									@click="viewClaimDetail(scope.row)">
									View
								</el-button>
							</div>
						</template>
					</el-table-column>
				</el-table>

				<!-- 分页 -->
				<el-col :span="24" class="toolbar">
					<el-pagination
						@size-change="handleMyClaimSizeChange"
						@current-change="handleMyClaimCurrentChange"
						:current-page="myClaimCurrentPage"
						:page-sizes="[10, 20, 50, 100]"
						:page-size="myClaimSize"
						layout="total, sizes, prev, pager, next, jumper"
						:total="myClaimTotal">
					</el-pagination>
				</el-col>
			</el-tab-pane>
		</el-tabs>

		<!-- 批量导入认领对话框 -->
	<el-dialog title="Batch Upload Claims (Nurse)" width="85%" :visible.sync="batchImportVisible" :close-on-click-modal="false">
		<el-form :model="batchImportForm">
			<el-form-item>
				<div style="margin-bottom: 10px;">
					<strong>Selected Inbound:</strong> {{selectedInboundInfo ? selectedInboundInfo.productName : 'Not selected'}}
					<span v-if="selectedInboundInfo" style="color: #909399; font-size: 12px;">({{selectedInboundInfo.inboundNo}})</span>
				</div>
			</el-form-item>
			<el-form-item>
				<el-input
					type="textarea"
					rows="12"
					v-model="batchImportText"
					:placeholder="batchImportPlaceholder">
				</el-input>
				<p class="note" style="margin-top: 10px;">
					<strong>Format: Tracking,Quantity or Tracking Quantity (one record per line)</strong>
					<br/>Supports separators: comma, space, or tab
					<br/>Examples:
					<br/>123456789012,5
					<br/>234567890123	10
					<br/>345678901234 3
				</p>
			</el-form-item>
		</el-form>
		<div slot="footer" class="dialog-footer">
			<el-button @click="batchImportVisible = false">Close</el-button>
			<el-button type="primary" @click="importBatchClaims" :loading="batchImportLoading">Import to Claim List</el-button>
		</div>
	</el-dialog>

	<!-- 批量导入认领对话框 - 神医端 -->
	<el-dialog title="Batch Upload Claims (Doctor)" width="85%" :visible.sync="doctorBatchImportVisible" :close-on-click-modal="false">
		<el-form :model="batchImportForm">
			<el-form-item>
				<div style="margin-bottom: 10px;">
					<strong>Selected Warehouse:</strong> {{claimForm.warehouseNo ? getWarehouseNameByNo(claimForm.warehouseNo) : 'Not selected'}}
				</div>
			</el-form-item>
			<el-form-item>
				<el-input
					type="textarea"
					rows="12"
					v-model="doctorBatchImportText"
					:placeholder="doctorBatchImportPlaceholder">
				</el-input>
				<p class="note" style="margin-top: 10px;">
					<strong>Format: Tracking,UPC,Quantity or Tracking UPC Quantity (one record per line)</strong>
					<br/>Supports separators: comma, space, or tab
					<br/>Examples:
					<br/>123456789012,012345678901,5
					<br/>234567890123	098765432109	10
					<br/>345678901234 111111111111 3
				</p>
			</el-form-item>
		</el-form>
		<div slot="footer" class="dialog-footer">
			<el-button @click="doctorBatchImportVisible = false">Close</el-button>
			<el-button type="primary" @click="importDoctorBatchClaims" :loading="doctorBatchImportLoading">Import to Claim List</el-button>
		</div>
	</el-dialog>

	<!-- 认领详情对话框 -->
	<el-dialog title="Claim Detail" :visible.sync="claimDetailDialog" width="60%">
		<div v-if="claimDetail">
			<el-descriptions title="Claim Information" :column="2" border>
				<el-descriptions-item label="Tracking #">{{claimDetail.tracking}}</el-descriptions-item>
				<el-descriptions-item label="UPC">{{claimDetail.upc}}</el-descriptions-item>
				<el-descriptions-item label="Original Claimed Qty">{{claimDetail.originalClaimedQty || claimDetail.claimedQty}}</el-descriptions-item>
				<el-descriptions-item label="Final Quantity">{{claimDetail.claimedQty}}</el-descriptions-item>
				<el-descriptions-item label="Claim Date">{{formatDate(claimDetail.claimDate)}}</el-descriptions-item>
				<el-descriptions-item label="Status">
					<el-tag :type="getClaimStatusTagType(claimDetail.claimStatus)">
						{{getClaimStatusText(claimDetail.claimStatus)}}
					</el-tag>
				</el-descriptions-item>
				<el-descriptions-item label="Review Note" :span="2">
					<p v-if="claimDetail.reviewReason">{{claimDetail.reviewReason}}</p>
					<span v-else class="note">No review note</span>
				</el-descriptions-item>
			</el-descriptions>
		</div>
	</el-dialog>

	<!-- 回到顶部浮动按钮 -->
	<transition name="fade">
		<el-button
			v-if="showScrollToTopButton"
			type="primary"
			icon="el-icon-arrow-up"
			circle
			class="scroll-to-top-button"
			@click.prevent="handleScrollToTopClick"
			title="Scroll to top">
		</el-button>
	</transition>

	<!-- Debug信息：测试显示隐藏 -->
	<div v-if="false" class="debug-scroll-info">
		showScrollToTopButton: {{ showScrollToTopButton }}, scrollY: {{ window.pageYOffset }}
	</div>
</section>
</template>

<script>
import { mixedPackageApi } from '../api/mixedPackage'
import { getNurseWarehouses, getCurrentWarehouse } from '../api/warehouseService'
import { getNurseDoctor } from '../api/memberService'
import { getAvailableInboundsForClaim } from '../api/inboundService'
import InboundStatusCard from '../components/InboundStatusCard.vue'

export default {
	components: {
		InboundStatusCard
	},
	data() {
		return {
			activeName: 'claim',
			
			// 认领表单
			claimForm: {
				inboundNo: null,
				tracking: '',
				upc: '',
				quantity: 1,
				warehouseNo: null
			},
			claimRules: {},
			claimLoading: false,
			addToListLoading: false,
			trackingInfo: null,
			warehouses: [],
			claimList: [], // 待提交的认领列表
			selectedClaims: [], // 选中的认领项目
			currentDoctorName: '', // 当前神医名称
			isNurse: false, // 是否为护士角色

			// Inbound相关数据
			availableInbounds: [], // 可用的Inbound列表
			availableInboundsLoading: false, // Inbound列表加载状态
			availableInboundsSearchTimer: null, // 搜索防抖定时器
			selectedInboundInfo: null, // 选中的Inbound详细信息

			// 我的认领记录
			myClaimList: [],
			myClaimListLoading: false,
			myClaimFilters: {
				tracking: '',
				upc: '',
				claimStatus: null
			},
			myClaimCurrentPage: 1,
			myClaimSize: 20,
			myClaimTotal: 0,
			
			// 认领详情对话框
			claimDetailDialog: false,
			claimDetail: null,

			// 批量导入相关 - 护士端
			batchImportVisible: false,
			batchImportLoading: false,
			batchImportText: '',
			batchImportForm: {},
			batchImportPlaceholder: 'Tracking,Quantity\r\n123456789012,5\r\n234567890123 10\r\n345678901234\t3',

			// 批量导入相关 - 神医端
			doctorBatchImportVisible: false,
			doctorBatchImportLoading: false,
			doctorBatchImportText: '',
			doctorBatchImportPlaceholder: 'Tracking,UPC,Quantity\r\n123456789012,012345678901,5\r\n234567890123 098765432109 10\r\n345678901234\t111111111111\t3',

			// 回到顶部按钮相关
			showScrollToTopButton: false,
			scrollHandler: null
		}
	},
	
	mounted() {
		// 初始化用户角色信息
		const userInfo = JSON.parse(window.sessionStorage.getItem('user') || '{}')
		this.isNurse = userInfo.roleId === '2'
		
		// 基于角色设置验证规则
		if (this.isNurse) {
			// 护士需要选择Inbound记录
			this.claimRules = {
				inboundNo: [{ required: true, message: 'Please select an inbound record', trigger: 'change' }],
				tracking: [
					{ required: true, message: 'Please enter tracking number', trigger: 'blur' },
					{ min: 12, message: 'Tracking number must be at least 12 digits', trigger: 'blur' },
					{ max: 34, message: 'Tracking number cannot exceed 34 digits', trigger: 'blur' }
				],
				upc: [{ required: true, message: 'Please enter UPC', trigger: 'blur' }],
				quantity: [{ required: true, message: 'Please enter quantity', trigger: 'blur' }],
				warehouseNo: [{ required: true, message: 'Please select warehouse', trigger: 'change' }]
			}
		} else {
			// 神医不需要选择Inbound记录
			this.claimRules = {
				tracking: [
					{ required: true, message: 'Please enter tracking number', trigger: 'blur' },
					{ min: 12, message: 'Tracking number must be at least 12 digits', trigger: 'blur' },
					{ max: 34, message: 'Tracking number cannot exceed 34 digits', trigger: 'blur' }
				],
				upc: [{ required: true, message: 'Please enter UPC', trigger: 'blur' }],
				quantity: [{ required: true, message: 'Please enter quantity', trigger: 'blur' }],
				warehouseNo: [{ required: true, message: 'Please select warehouse', trigger: 'change' }]
			}
		}
		
		// 加载保存的认领列表
		this.loadClaimListFromStorage()
		
		this.getMyClaimList()
		this.getWarehouseList()
		
		// 只有护士需要加载Available Inbounds
		if (this.isNurse) {
			this.loadAvailableInbounds()
		}
		
		// 检查是否有从URL传来的tracking号码
		const trackingFromQuery = this.$route.query.tracking
		if (trackingFromQuery) {
			this.claimForm.tracking = trackingFromQuery
			// 自动搜索tracking信息
			this.searchTrackingInfo()
			// 显示提示信息
			this.$message.success(`Pre-filled tracking number: ${trackingFromQuery}. Please enter UPC, quantity and warehouse to claim.`)
		}

		// 添加scroll事件监听 - 使用防抖
		// 监听实际的scrollable容器：.content-container（在Home.vue中定义）
		const contentContainer = document.querySelector('.content-container')
		let lastScrollTime = 0
		this.scrollHandler = () => {
			const now = Date.now()
			if (now - lastScrollTime > 100) {
				this.handleScroll()
				lastScrollTime = now
			}
		}

		if (contentContainer) {
			// 如果找到了.content-container，监听其scroll事件
			contentContainer.addEventListener('scroll', this.scrollHandler, false)
		} else {
			// 备用方案：监听window的scroll事件
			window.addEventListener('scroll', this.scrollHandler, false)
		}

		// 初始检查一次
		this.handleScroll()
	},

	beforeDestroy() {
		// 组件销毁时移除scroll事件监听
		if (this.scrollHandler) {
			const contentContainer = document.querySelector('.content-container')
			if (contentContainer) {
				// 从.content-container移除监听
				contentContainer.removeEventListener('scroll', this.scrollHandler, false)
			} else {
				// 从window移除监听（备用方案）
				window.removeEventListener('scroll', this.scrollHandler, false)
			}
		}
	},

	methods: {
		// ============ 本地存储相关方法 ============
		getStorageKey() {
			const userInfo = JSON.parse(window.sessionStorage.getItem('user') || '{}')
			return `mixedPackageClaims_${userInfo.userNo || 'unknown'}`
		},
		
		loadClaimListFromStorage() {
			try {
				const storageKey = this.getStorageKey()
				const savedClaims = localStorage.getItem(storageKey)
				if (savedClaims) {
					const claims = JSON.parse(savedClaims)
					// 验证数据结构并限制最大数量
					if (Array.isArray(claims) && claims.length <= 1000) {
						this.claimList = claims
						if (claims.length > 0) {
							this.$message.info(`Restored ${claims.length} items from previous session`)
						}
					}
				}
			} catch (error) {
				this.claimList = []
			}
		},
		
		saveClaimListToStorage() {
			try {
				const storageKey = this.getStorageKey()
				localStorage.setItem(storageKey, JSON.stringify(this.claimList))
			} catch (error) {
			}
		},
		
		clearClaimListFromStorage() {
			try {
				const storageKey = this.getStorageKey()
				localStorage.removeItem(storageKey)
			} catch (error) {
			}
		},
		
		// ============ 认领相关方法 ============
		searchTrackingInfo() {
			if (!this.claimForm.tracking) {
				this.trackingInfo = null
				return
			}
			
			// 搜索快递单信息（不显示具体商品信息，只显示包裹基本信息）
			const params = {
				tracking: this.claimForm.tracking,
				currentCompany: window.sessionStorage.getItem("currentCompany") // 传递当前选择的神医/公司信息
			}
			
			mixedPackageApi.searchClaimableItems(params).then(res => {
				if (res.code === 200 || res.code === '200' || res.code === '200') {
					// 只显示包裹信息，不显示可认领数量
					const data = res.result || res.data
					if (data && data.length > 0) {
						this.trackingInfo = {
							tracking: this.claimForm.tracking,
							warehouse: data[0].warehouse,
							packageStatus: data[0].packageStatus,
							totalItems: data[0].totalItems,
							note: data[0].note
						}
					} else {
						this.trackingInfo = null
					}
				}
			}).catch(() => {
				this.trackingInfo = null
			})
		},
		
		
		resetClaimForm() {
			this.claimForm = {
				inboundNo: null,
				tracking: '',
				upc: '',
				quantity: 1,
				warehouseNo: null
			}
			this.trackingInfo = null
			this.$refs.claimForm.resetFields()
		},
		
		// ============ 我的认领记录相关方法 ============
		getMyClaimList() {
			this.myClaimListLoading = true
			const params = {
				current: this.myClaimCurrentPage,
				size: this.myClaimSize,
				...this.myClaimFilters
			}
			
			mixedPackageApi.getMyClaimList(params).then(res => {
				if (res.code === 200 || res.code === '200') {
					this.myClaimList = res.result.records || (res.data && res.data.records) || []
					this.myClaimTotal = res.result.total || (res.data && res.data.total) || 0
				}
				this.myClaimListLoading = false
			}).catch(() => {
				this.myClaimListLoading = false
			})
		},
		
		handleMyClaimSizeChange(val) {
			this.myClaimSize = val
			this.getMyClaimList()
		},
		
		handleMyClaimCurrentChange(val) {
			this.myClaimCurrentPage = val
			this.getMyClaimList()
		},
		
		cancelClaim(row) {
			this.$confirm('Are you sure to cancel this claim?', 'Confirm', {
				type: 'warning'
			}).then(() => {
				mixedPackageApi.cancelClaim(row.claimId).then(res => {
					if (res.code === 200 || res.code === '200') {
						this.$message.success('Claim cancelled successfully')
						this.getMyClaimList()
					} else {
						this.$message.error(res.message || 'Cancel failed')
					}
				})
			})
		},
		
		viewClaimDetail(row) {
			this.claimDetail = row
			this.claimDetailDialog = true
		},
		
		// ============ Inbound相关方法 ============
		// 获取可用的Inbound列表 - 只返回未完成的
		async loadAvailableInbounds() {
			try {
				const response = await getAvailableInboundsForClaim({
					doctorNo: window.sessionStorage.getItem("currentCompany")
				})
				
				if (response.code === 200 || response.code === '200') {
					// 后端已经过滤，但前端再次确保只有status=0的记录
					const data = response.result || response.data || []
					this.availableInbounds = data.filter(inbound => 
						inbound.status === 0 && inbound.leftNum > 0
					)
					
					if (this.availableInbounds.length === 0) {
						this.$message.info('No incomplete inbound records available for claiming')
					}
				}
			} catch (error) {
				this.$message.error('Failed to load available inbounds: ' + (error.message || 'Unknown error'))
			}
		},

		// 远程搜索可用于混寄包裹认领的Inbound列表(支持UPC过滤)
		async searchAvailableInbounds(upc = '') {
			// 清除之前的定时器
			if (this.availableInboundsSearchTimer) {
				clearTimeout(this.availableInboundsSearchTimer)
			}

			// 如果清空搜索，清空列表
			if (upc === '' || upc === null || upc === undefined) {
				this.availableInbounds = []
				return
			}

			// 最少输入4位才调用后台接口
			if (upc.length < 4) {
				return
			}

			// 300ms防抖延迟
			this.availableInboundsSearchTimer = setTimeout(async () => {
				this.availableInboundsLoading = true
				try {
					const response = await getAvailableInboundsForClaim({
						doctorNo: window.sessionStorage.getItem("currentCompany"),
						upc: upc.trim()
					})

					if (response.code === 200 || response.code === '200') {
						const data = response.result || response.data || []
						// 后端已经过滤，但前端再次确保只有status=0的记录
						this.availableInbounds = data.filter(inbound =>
							inbound.status === 0 && inbound.leftNum > 0
						)

						if (this.availableInbounds.length === 0) {
							this.$message.info('No available inbounds found for this UPC')
						}
					} else {
						this.$message.warning('Failed to search inbounds')
						this.availableInbounds = []
					}
				} catch (error) {
					this.availableInboundsLoading = false
					console.error('Error searching available inbounds:', error)
					this.$message.error('Failed to search available inbounds: ' + (error.message || 'Unknown error'))
					this.availableInbounds = []
				} finally {
					this.availableInboundsLoading = false
				}
			}, 300)
		},

		// Inbound选择变化时的处理
		onInboundChange(inboundNo) {
			this.selectedInboundInfo = this.availableInbounds.find(i => i.inboundNo === inboundNo)
			
			// 如果没有选择Inbound（清空选择），清空相关字段
			if (!inboundNo || !this.selectedInboundInfo) {
				this.selectedInboundInfo = null
				this.claimForm.upc = ''
				this.claimForm.warehouseNo = null
				return
			}
			
			// 验证选中的Inbound是否还有剩余可认领数量
			if (this.selectedInboundInfo.leftNum <= 0) {
				this.$message.warning('This inbound record has no remaining quantity to claim')
				this.claimForm.inboundNo = null
				this.selectedInboundInfo = null
				this.claimForm.upc = ''
				this.claimForm.warehouseNo = null
			} else {
				// 自动设置仓库
				const inboundWarehouseNo = this.selectedInboundInfo.warehouseNo
				this.claimForm.warehouseNo = inboundWarehouseNo
				
				// 验证仓库是否在可选列表中
				let warehouseFound = false
				let warehouseName = ''
				let warehouseGroup = ''
				for (const group of this.warehouses) {
					const warehouse = group.options.find(w => w.warehouseNo === inboundWarehouseNo)
					if (warehouse) {
						warehouseFound = true
						warehouseName = `${warehouse.state} ${warehouse.city} ${warehouse.address}`
						warehouseGroup = group.label
						break
					}
				}
				
				// 自动填入UPC
				if (this.selectedInboundInfo.upc) {
					this.claimForm.upc = this.selectedInboundInfo.upc
					
					// 组合提示信息 - 只有找到仓库时才显示完整信息
					if (warehouseFound) {
						const groupInfo = warehouseGroup === 'Current Doctor Warehouses' ? '' : ` (${warehouseGroup})`
						this.$message.success(`Auto-filled - UPC: ${this.selectedInboundInfo.upc} | Warehouse: ${warehouseName}${groupInfo}`)
					} else {
						// 仓库未找到但不显示警告，因为用户确认仓库在下拉列表中可选
						this.$message.success(`Auto-filled - UPC: ${this.selectedInboundInfo.upc}`)
					}
				} else {
					this.claimForm.upc = ''
					if (warehouseFound) {
						const groupInfo = warehouseGroup === 'Current Doctor Warehouses' ? '' : ` (${warehouseGroup})`
						this.$message.success(`Warehouse auto-selected: ${warehouseName}${groupInfo}`)
					}
					this.$message.warning('Selected inbound record has no UPC information')
				}
			}
		},
		
		// 获取当前可认领的最大数量
		getMaxClaimableQty() {
			return this.selectedInboundInfo ? this.selectedInboundInfo.leftNum : 999
		},
		
		// 验证UPC匹配
		validateUpcMatch() {
			if (this.selectedInboundInfo && this.claimForm.upc) {
				if (!this.selectedInboundInfo.upc) {
					this.$message.warning('Selected inbound record has no UPC information')
					return
				}
				if (this.selectedInboundInfo.upc !== this.claimForm.upc) {
					this.$message.warning(`UPC mismatch! Expected: ${this.selectedInboundInfo.upc}, Entered: ${this.claimForm.upc}`)
				}
			}
		},
		
		// ============ 工具方法 ============
		getUpcPlaceholder() {
			if (this.isNurse) {
				if (this.selectedInboundInfo && this.selectedInboundInfo.upc) {
					return `🔒 Auto-filled from Inbound (read-only)`
				} else if (this.selectedInboundInfo) {
					return 'Selected Inbound has no UPC'
				} else {
					return 'Select an Inbound record first'
				}
			} else {
				return 'Enter UPC'
			}
		},
		
		getWarehousePlaceholder() {
			if (this.isNurse) {
				if (this.selectedInboundInfo && this.selectedInboundInfo.warehouseNo) {
					return `🔒 Auto-selected from Inbound`
				} else if (this.selectedInboundInfo) {
					return 'Loading warehouse from Inbound...'
				} else {
					return 'Select an Inbound record first'
				}
			} else {
				return 'Select warehouse'
			}
		},
		
		formatDate(date) {
			if (!date) return ''
			return new Date(date).toLocaleString()
		},
		
		getStatusText(status) {
			const statusMap = {
				0: 'Pending',
				1: 'Partial Claimed', 
				2: 'All Claimed',
				3: 'Completed'
			}
			return statusMap[status] || 'Unknown'
		},
		
		getStatusTagType(status) {
			const typeMap = {
				0: '',
				1: 'warning',
				2: 'success',
				3: 'info'
			}
			return typeMap[status] || ''
		},
		
		getClaimStatusTagType(status) {
			const typeMap = {
				0: 'warning',
				1: 'success',
				2: 'info',
				3: 'danger',
				4: 'primary'
			}
			return typeMap[status] || ''
		},
		
		getClaimStatusText(status) {
			const statusMap = {
				0: 'Pending Review',
				1: 'Approved',
				2: 'Distributed',
				3: 'Rejected',
				4: 'Quantity Adjusted'
			}
			return statusMap[status] || 'Unknown'
		},
		
		// ============ 仓库管理相关方法 ============
		getWarehouseList() {
			// 获取用户角色信息
			const userInfo = JSON.parse(window.sessionStorage.getItem('user') || '{}')
			const userRoleId = userInfo.roleId
			const currentCompany = window.sessionStorage.getItem("currentCompany")
			
			if (userRoleId === '1') {
				// 神医：使用混寄包裹专用的可认领仓库API
				mixedPackageApi.getAccessibleWarehouses({
					currentCompany: currentCompany
				}).then((res) => {
					if (res.code === 200 || res.code === '200') {
						this.warehouses = []
						let doctorWarehouses = {
							label: 'Accessible Warehouses',
							options: res.result || []
						}
						if (doctorWarehouses.options.length > 0) {
							this.warehouses.push(doctorWarehouses)
						}
					} else {
						this.$message.error('获取仓库列表失败，请联系管理员')
					}
				}).catch(error => {
					this.$message.error('获取仓库列表失败，请联系管理员')
				})
			} else if (userRoleId === '2') {
				// 护士：获取护士可访问的仓库权限
				let param = {
					doctorNo: currentCompany
				}
				
				getNurseWarehouses(param).then((res) => {
					if (res.status === 200) {
						this.warehouses = []
						let currentDoctorWarehouses = {
							label: 'Current Doctor Warehouses',
							options: []
						}
						let accessibleWarehouses = {
							label: 'Accessible Warehouses for Claims',
							options: []
						}
						
						res.result.forEach((item) => {
							// isDoctor === 0 表示神医的仓库
							if (item.isDoctor === 0) {
								if (item.userNo === currentCompany) {
									// 当前选择神医的仓库
									currentDoctorWarehouses.options.push(item)
								} else {
									// 其他神医的仓库（护士可能有Inbound记录在这些仓库）
									accessibleWarehouses.options.push(item)
								}
							}
						})
						
						// 优先显示当前神医的仓库
						if (currentDoctorWarehouses.options.length > 0) {
							this.warehouses.push(currentDoctorWarehouses)
						}
						// 然后显示其他可访问的仓库
						if (accessibleWarehouses.options.length > 0) {
							this.warehouses.push(accessibleWarehouses)
						}
						
						// 获取当前神医名称用于显示
						this.getCurrentDoctorName()
					} else {
						this.$message.error('获取仓库列表失败，请联系管理员')
					}
				}).catch(error => {
					this.$message.error('获取仓库列表失败，请联系管理员')
				})
			}
		},
		
		getCurrentDoctorName() {
			const currentCompany = window.sessionStorage.getItem("currentCompany")
			if (currentCompany) {
				getNurseDoctor().then((res) => {
					if (res.status === 200 && res.result) {
						const currentDoctor = res.result.find(doctor => doctor.userNo === currentCompany)
						if (currentDoctor) {
							this.currentDoctorName = currentDoctor.userName
						}
					}
				}).catch(error => {
					this.currentDoctorName = 'Current Doctor'
				})
			}
		},
		
		// Inbound状态刷新回调
		onInboundStatusRefreshed(statusData) {
			// 更新selectedInboundInfo的leftNum以反映最新的可认领数量
			if (this.selectedInboundInfo && this.selectedInboundInfo.inboundNo === statusData.inboundNo) {
				// 刷新可用的Inbound列表以获取最新数据
				this.loadAvailableInbounds()
				
				this.$message.info(`Status refreshed: ${statusData.available} items available for claiming`)
			}
		},
		
		
		// ============ 批量认领相关方法 ============
		addToClaimList() {
			// 检查数量限制
			if (this.claimList.length >= 1000) {
				this.$message.error('Maximum 1000 items allowed in claim list. Please submit current items or clear the list to add more.')
				return
			}
			
			this.$refs.claimForm.validate(valid => {
				if (valid) {
					// 护士需要验证Inbound相关信息
					if (this.isNurse) {
						// 验证Inbound是否选择
						if (!this.selectedInboundInfo) {
							this.$message.warning('Please select an inbound record first')
							return
						}
						
						// 验证UPC匹配
						if (!this.selectedInboundInfo.upc) {
							this.$message.warning('Selected inbound record has no UPC information')
							return
						}
						if (this.selectedInboundInfo.upc !== this.claimForm.upc) {
							this.$message.warning(`UPC mismatch! Expected: ${this.selectedInboundInfo.upc}, Entered: ${this.claimForm.upc}`)
							return
						}
						
						// 验证数量不超过可认领数量
						if (this.claimForm.quantity > this.selectedInboundInfo.leftNum) {
							this.$message.warning(`Quantity exceeds remaining claimable amount (${this.selectedInboundInfo.leftNum})`)
							return
						}
					}
					
					// 检查是否已存在相同的认领项
					const existingItem = this.claimList.find(item => 
						item.tracking === this.claimForm.tracking && 
						(this.isNurse ? item.inboundNo === this.claimForm.inboundNo : item.upc === this.claimForm.upc)
					)
					
					if (existingItem) {
						if (this.isNurse) {
							this.$message.warning('This tracking number is already claimed for this inbound record')
						} else {
							this.$message.warning('This tracking number is already claimed for this UPC')
						}
						return
					}
					
					// 获取仓库名称
					let selectedWarehouse = null
					for (const group of this.warehouses) {
						selectedWarehouse = group.options.find(w => w.warehouseNo === this.claimForm.warehouseNo)
						if (selectedWarehouse) break
					}
					const warehouseName = selectedWarehouse ? 
						`${selectedWarehouse.state} ${selectedWarehouse.city} ${selectedWarehouse.address}` : 
						'Unknown Warehouse'
					
					// 构建认领项目数据
					const claimItem = {
						tracking: this.claimForm.tracking,
						upc: this.claimForm.upc,
						quantity: this.claimForm.quantity,
						warehouseNo: this.claimForm.warehouseNo,
						warehouseName: warehouseName
					}
					
					// 护士需要添加Inbound相关信息
					if (this.isNurse && this.selectedInboundInfo) {
						claimItem.inboundNo = this.claimForm.inboundNo
						claimItem.inboundInfo = `${this.selectedInboundInfo.productName} (${this.selectedInboundInfo.inboundNo})`
						claimItem.productName = this.selectedInboundInfo.productName
						claimItem.locked = true // 锁定Inbound关联
					} else {
						// 神医的认领项目显示UPC作为产品信息
						claimItem.inboundInfo = `UPC: ${this.claimForm.upc}`
						claimItem.productName = `Mixed Package - ${this.claimForm.upc}`
					}
					
					// 添加到认领列表
					this.claimList.push(claimItem)
					
					// 保存到本地存储
					this.saveClaimListToStorage()
					
					this.$message.success('Added to claim list successfully')
					
					// 清空表单
					this.claimForm.tracking = ''
					this.claimForm.quantity = 1
					
					if (this.isNurse) {
						// 护士：保持Inbound选择、warehouse和UPC，方便连续添加同一Inbound的多个tracking
						// 不清空inboundNo、warehouseNo和UPC
					} else {
						// 神医：清空UPC，方便输入新的UPC
						this.claimForm.upc = ''
					}
				}
			})
		},
		
		removeFromClaimList(index) {
			const removedItem = this.claimList[index]
			this.claimList.splice(index, 1)
			
			// 同时从选中列表中移除
			this.selectedClaims = this.selectedClaims.filter(item => 
				!(item.tracking === removedItem.tracking && 
				  item.upc === removedItem.upc && 
				  item.warehouseNo === removedItem.warehouseNo)
			)
			
			// 保存到本地存储
			this.saveClaimListToStorage()
			
			this.$message.success('Item removed from claim list')
		},
		
		clearClaimList() {
			this.$confirm('Are you sure to clear all claim items?', 'Confirm', {
				type: 'warning'
			}).then(() => {
				this.claimList = []
				this.selectedClaims = []
				// 清理本地存储
				this.clearClaimListFromStorage()
				this.$message.success('Claim list cleared')
			})
		},
		
		handleSelectionChange(selection) {
			this.selectedClaims = selection
		},
		
		selectAllClaims() {
			this.$refs.claimTable.toggleAllSelection()
		},
		
		clearSelection() {
			if (this.selectedClaims.length === 0) {
				return
			}
			
			// 复制选中项目数组，避免删除过程中数组被修改
			const itemsToRemove = [...this.selectedClaims]
			
			// 遍历选中项目，逐个调用单个删除函数
			itemsToRemove.forEach(selectedItem => {
				// 找到选中项在claimList中的索引
				const index = this.claimList.findIndex(item => 
					item.tracking === selectedItem.tracking && 
					item.upc === selectedItem.upc && 
					item.warehouseNo === selectedItem.warehouseNo
				)
				
				if (index !== -1) {
					// 调用已有的单个删除函数
					this.removeFromClaimList(index)
				}
			})
			
			this.$message.success(`Removed ${itemsToRemove.length} selected items`)
		},
		
		async submitSelectedClaims() {
			if (this.selectedClaims.length === 0) {
				this.$message.warning('Please select items to submit')
				return
			}
			
			const confirmed = await this.$confirm(`Are you sure to submit ${this.selectedClaims.length} selected claim items?`, 'Confirm Submission', {
				type: 'warning'
			}).catch(() => false)
			
			if (!confirmed) return
			
			this.claimLoading = true
			let successCount = 0
			let failCount = 0
			const successfulItems = []
			const failedItems = []
			const totalItems = this.selectedClaims.length
			
			// 串行处理每个认领，避免并发冲突
			for (let i = 0; i < this.selectedClaims.length; i++) {
				const item = this.selectedClaims[i]
				const params = {
					tracking: item.tracking,
					upc: item.upc,
					quantity: item.quantity,
					warehouseNo: item.warehouseNo,
					targetDoctorNo: window.sessionStorage.getItem("currentCompany"), // 目标神医编号
					currentCompany: window.sessionStorage.getItem("currentCompany") // 当前选择的公司，用于权限验证
				}
				
				// 只有护士的认领才包含inboundNo
				if (this.isNurse && item.inboundNo) {
					params.inboundNo = item.inboundNo // 关联的Inbound记录编号
				}
				
				try {
					// 显示当前处理进度
					this.$message.info(`Processing claim ${i + 1}/${totalItems}: ${item.upc}`, {
						duration: 1000,
						showClose: true
					})
					
					const res = await mixedPackageApi.submitClaim(params)
					
					if (res.code === 200 || res.code === '200') {
						successCount++
						successfulItems.push(item)
					} else {
						failCount++
						failedItems.push({
							...item,
							errorMessage: res.message || res.result || 'Unknown error'
						})
					}
				} catch (error) {
					failCount++
					failedItems.push({
						...item,
						errorMessage: error.message || 'Network error'
					})
				}
				
				// 每个请求之间稍作延迟，减少服务器压力
				if (i < this.selectedClaims.length - 1) {
					await new Promise(resolve => setTimeout(resolve, 200))
				}
			}
			
			this.claimLoading = false
			
			// 从claim列表中移除成功的项目
			this.claimList = this.claimList.filter(item => 
				!successfulItems.some(successItem => 
					successItem.tracking === item.tracking && 
					successItem.upc === item.upc && 
					successItem.warehouseNo === item.warehouseNo
				)
			)
			
			// 更新本地存储
			this.saveClaimListToStorage()
			
			// 清空选中状态
			this.selectedClaims = []
			
			// 如果所有认领都成功且列表为空，清理本地存储
			if (failCount === 0 && this.claimList.length === 0) {
				this.clearClaimListFromStorage()
			}
			
			// 显示最终结果
			if (failCount === 0) {
				this.$message.success(`All ${successCount} claims submitted successfully!`)
			} else if (successCount === 0) {
				this.$message.error(`All ${failCount} claims failed to submit.`)
				// 显示失败原因详情 - 改进显示格式
				this.showDetailedErrors(failedItems, 'Submission Errors')
			} else {
				this.$message.warning(`${successCount} claims succeeded, ${failCount} claims failed. Failed items remain in the list.`)
				// 显示失败项目的详细信息 - 改进显示格式
				this.showDetailedErrors(failedItems, 'Failed Claims', 'warning')
			}
			
			// 刷新我的认领记录（如果有成功的认领）
			if (successCount > 0) {
				this.getMyClaimList()
			}
		},

		// ============ 批量导入相关方法 ============
		showBatchImportDialog() {
			if (!this.isNurse || !this.claimForm.inboundNo) {
				this.$message.warning('Please select an Inbound record first')
				return
			}

			this.batchImportText = ''
			this.batchImportVisible = true
		},

		importBatchClaims() {
			if (!this.isNurse || !this.claimForm.inboundNo || !this.selectedInboundInfo) {
				this.$message.error('Please select an Inbound record first')
				return
			}

			this.batchImportLoading = true
			let text = this.batchImportText.trim()

			if (!text) {
				this.$message.error('Please enter tracking data')
				this.batchImportLoading = false
				return
			}

			// 检查数量限制
			const remainingCapacity = 1000 - this.claimList.length
			if (remainingCapacity < 0) {
				this.$message.error('Claim list is full. Maximum 1000 items. Please submit current items to add more.')
				this.batchImportLoading = false
				return
			}

			try {
				// 解析文本数据
				const parsedItems = this.parseTextData(text)

				if (parsedItems.length === 0) {
					this.$message.error('No valid tracking data found')
					this.batchImportLoading = false
					return
				}

				// 检查解析后的数量是否超过容量
				if (parsedItems.length > remainingCapacity) {
					this.$message.warning(`Only ${remainingCapacity} items can be added. Claim list can hold maximum 1000 items total. Processing first ${remainingCapacity} items.`)
				}

				// 验证并添加到认领列表
				const { validItems, invalidItems, duplicateItems } = this.validateBatchItems(parsedItems)

				// 显示验证结果
				if (invalidItems.length > 0) {
					const errorMsg = `${invalidItems.length} invalid items: ${invalidItems.map(item => item.tracking + ":" + item.reason).join('\n')}`
					// this.$message.warning(errorMsg)
					this.$message({
						type: 'warning',
						dangerouslyUseHTMLString: true,
						message: errorMsg.replace(/\n/g, '<br>')
					})
					return
				}

				if (duplicateItems.length > 0) {
					const duplicateMsg = `${duplicateItems.length} duplicate/existing items: ${duplicateItems.join(', ')}`
					this.$message.info(duplicateMsg)
					return
				}

				// 添加有效项到认领列表
				let addedCount = 0
				for (let i = 0; i < validItems.length; i++) {
					if (this.claimList.length >= 1000) {
						this.$message.warning(`Reached maximum capacity (1000 items). Added ${addedCount} items.`)
						break
					}

					const item = validItems[i]

					// 构建完整的认领项目
					const claimItem = {
						tracking: item.tracking,
						upc: this.selectedInboundInfo.upc,
						quantity: item.quantity || 1,
						warehouseNo: this.selectedInboundInfo.warehouseNo,
						warehouseName: this.getWarehouseNameByNo(this.selectedInboundInfo.warehouseNo),
						inboundNo: this.claimForm.inboundNo,
						inboundInfo: `${this.selectedInboundInfo.productName} (${this.selectedInboundInfo.inboundNo})`,
						productName: this.selectedInboundInfo.productName,
						locked: true
					}

					this.claimList.push(claimItem)
					addedCount++
				}

				// 保存到本地存储
				this.saveClaimListToStorage()

				// 关闭对话框
				this.batchImportVisible = false
				this.batchImportText = ''

				// 显示成功消息
				let message = `Successfully added ${addedCount} items to claim list`
				this.$message.success(message)

			} catch (error) {
				this.$message.error(`Error parsing data: ${error.message}`)
			} finally {
				this.batchImportLoading = false
			}
		},

		// 解析文本数据 - 格式：Tracking,Quantity（每行一条记录）
		// 支持分隔符：逗号、空格、制表符（任意组合）
		parseTextData(text) {
			const items = []
			const lines = text.split('\n')

			lines.forEach((line, index) => {
				line = line.trim()

				// 跳过空行和标题行
				if (!line || line.toLowerCase().includes('tracking')) {
					return
				}

				// 优先按逗号分割（最准确）
				let parts = []
				if (line.includes(',')) {
					parts = line.split(',').map(p => p.trim()).filter(p => p)
				} else {
					// 如果没有逗号，按空格/Tab分割（兼容Excel复制粘贴）
					parts = line.split(/[\s\t]+/).filter(p => p)
				}

				if (parts.length >= 1) {
					const tracking = parts[0]
					const quantity = parseInt(parts[1]) || 1

					items.push({
						tracking,
						quantity,
						lineNo: index + 1
					})
				}
			})

			return items
		},

		// 验证批量项目 - 检查格式、重复等
		validateBatchItems(items) {
			const validItems = []
			const invalidItems = []
			const duplicateItems = []
			const trackingSet = new Set()

			items.forEach(item => {
				const tracking = item.tracking || ''
				const quantity = item.quantity || 1

				// 检查tracking不为空
				if (!tracking) {
					invalidItems.push({ ...item, reason: 'Empty tracking number' })
					return
				}

				// 检查tracking长度 (12-34字符)
				if (tracking.length < 12 || tracking.length > 34) {
					invalidItems.push({
						tracking,
						reason: `Tracking length must be 12-34 characters, got ${tracking.length}`
					})
					return
				}

				// 检查数量有效性
				if (quantity < 1 || quantity > 10000 || !Number.isInteger(quantity)) {
					invalidItems.push({
						tracking,
						reason: `Invalid quantity(1 ~ 10000): ${quantity}`
					})
					return
				}

				// 检查重复
				if (trackingSet.has(tracking)) {
					duplicateItems.push(tracking)
					return
				}

				// 检查是否已存在于claim list中
				const exists = this.claimList.find(claimItem =>
					claimItem.tracking === tracking && claimItem.inboundNo === this.claimForm.inboundNo
				)
				if (exists) {
					duplicateItems.push(tracking)
					return
				}

				// 有效项目
				trackingSet.add(tracking)
				validItems.push(item)
			})

			return { validItems, invalidItems, duplicateItems }
		},

		// 获取仓库名称 - 根据仓库号
		getWarehouseNameByNo(warehouseNo) {
			for (const group of this.warehouses) {
				const warehouse = group.options.find(w => w.warehouseNo === warehouseNo)
				if (warehouse) {
					return `${warehouse.state} ${warehouse.city} ${warehouse.address}`
				}
			}
			return 'Unknown Warehouse'
		},

		// ============ Doctor Batch Import Methods ============
		showDoctorBatchImportDialog() {
			if (this.isNurse || !this.claimForm.warehouseNo) {
				this.$message.warning('Please select a Warehouse first')
				return
			}

			this.doctorBatchImportText = ''
			this.doctorBatchImportVisible = true
		},

		importDoctorBatchClaims() {
			if (this.isNurse || !this.claimForm.warehouseNo) {
				this.$message.error('Please select a Warehouse first')
				return
			}

			this.doctorBatchImportLoading = true
			let text = this.doctorBatchImportText.trim()

			if (!text) {
				this.$message.error('Please enter tracking data')
				this.doctorBatchImportLoading = false
				return
			}

			// Check capacity limit
			const remainingCapacity = 1000 - this.claimList.length
			if (remainingCapacity <= 0) {
				this.$message.error('Claim list is full. Maximum 1000 items. Please submit current items to add more.')
				this.doctorBatchImportLoading = false
				return
			}

			try {
				// Parse text data
				const parsedItems = this.parseDoctorTextData(text)

				if (parsedItems.length === 0) {
					this.$message.error('No valid tracking data found')
					this.doctorBatchImportLoading = false
					return
				}

				// Check if parsed items exceed capacity
				if (parsedItems.length > remainingCapacity) {
					this.$message.warning(`Only ${remainingCapacity} items can be added. Claim list can hold maximum 1000 items total. Processing first ${remainingCapacity} items.`)
				}

				// Validate and add to claim list
				const { validItems, invalidItems, duplicateItems } = this.validateDoctorBatchItems(parsedItems)

				// Display validation results
				if (invalidItems.length > 0) {
					const errorMsg = `${invalidItems.length} invalid items: ${invalidItems.map(item => item.tracking).join(', ')}`
					this.$message.warning(errorMsg)
				}

				if (duplicateItems.length > 0) {
					const duplicateMsg = `${duplicateItems.length} duplicate/existing items: ${duplicateItems.join(', ')}`
					this.$message.info(duplicateMsg)
				}

				// Add valid items to claim list
				let addedCount = 0
				for (let i = 0; i < validItems.length; i++) {
					if (this.claimList.length >= 1000) {
						this.$message.warning(`Reached maximum capacity (1000 items). Added ${addedCount} items.`)
						break
					}

					const item = validItems[i]

					// Build complete claim item
					const claimItem = {
						tracking: item.tracking,
						upc: item.upc,
						quantity: item.quantity || 1,
						warehouseNo: this.claimForm.warehouseNo,
						warehouseName: this.getWarehouseNameByNo(this.claimForm.warehouseNo),
						productName: `Mixed Package - ${item.upc}`
					}

					this.claimList.push(claimItem)
					addedCount++
				}

				// Save to local storage
				this.saveClaimListToStorage()

				// Close dialog
				this.doctorBatchImportVisible = false
				this.doctorBatchImportText = ''

				// Display success message
				let message = `Successfully added ${addedCount} items to claim list`
				if (invalidItems.length > 0) {
					message += ` (${invalidItems.length} invalid items skipped)`
				}
				if (duplicateItems.length > 0) {
					message += ` (${duplicateItems.length} duplicates skipped)`
				}
				this.$message.success(message)

			} catch (error) {
				this.$message.error(`Error parsing data: ${error.message}`)
			} finally {
				this.doctorBatchImportLoading = false
			}
		},

		// Parse doctor text data - Format: Tracking,UPC,Quantity (one record per line)
		// Supports separators: comma, space, tab
		parseDoctorTextData(text) {
			const items = []
			const lines = text.split('\n')

			lines.forEach((line, index) => {
				line = line.trim()

				// Skip empty lines and header rows
				if (!line || line.toLowerCase().includes('tracking')) {
					return
				}

				// Try comma separation first (most accurate)
				let parts = []
				if (line.includes(',')) {
					parts = line.split(',').map(p => p.trim()).filter(p => p)
				} else {
					// If no comma, split by space/tab (compatible with Excel copy-paste)
					parts = line.split(/[\s\t]+/).filter(p => p)
				}

				if (parts.length >= 2) {
					const tracking = parts[0]
					const upc = parts[1]
					const quantity = parseInt(parts[2]) || 1

					items.push({
						tracking,
						upc,
						quantity,
						lineNo: index + 1
					})
				}
			})

			return items
		},

		// Validate doctor batch items - Check format, duplicates, etc.
		validateDoctorBatchItems(items) {
			const validItems = []
			const invalidItems = []
			const duplicateItems = []
			const trackingUpcSet = new Set()

			items.forEach(item => {
				const tracking = item.tracking || ''
				const upc = item.upc || ''
				const quantity = item.quantity || 1

				// Check tracking is not empty
				if (!tracking) {
					invalidItems.push({ ...item, reason: 'Empty tracking number' })
					return
				}

				// Check tracking length (12-34 characters)
				if (tracking.length < 12 || tracking.length > 34) {
					invalidItems.push({
						tracking,
						reason: `Tracking length must be 12-34 characters, got ${tracking.length}`
					})
					return
				}

				// Check UPC is not empty
				if (!upc) {
					invalidItems.push({ ...item, reason: 'Empty UPC' })
					return
				}

				// Check UPC length (5-20 characters)
				if (upc.length < 5 || upc.length > 20) {
					invalidItems.push({
						tracking,
						upc,
						reason: `UPC length must be 5-20 characters, got ${upc.length}`
					})
					return
				}

				// Check quantity validity
				if (quantity < 1 || quantity > 10000 || !Number.isInteger(quantity)) {
					invalidItems.push({
						tracking,
						reason: `Invalid quantity: ${quantity}`
					})
					return
				}

				// Check for duplicates within this batch
				const trackingUpcKey = `${tracking}|${upc}`
				if (trackingUpcSet.has(trackingUpcKey)) {
					duplicateItems.push(tracking)
					return
				}

				// Check if already exists in claim list
				const exists = this.claimList.find(claimItem =>
					claimItem.tracking === tracking &&
					claimItem.upc === upc &&
					claimItem.warehouseNo === this.claimForm.warehouseNo
				)
				if (exists) {
					duplicateItems.push(tracking)
					return
				}

				// Valid item
				trackingUpcSet.add(trackingUpcKey)
				validItems.push(item)
			})

			return { validItems, invalidItems, duplicateItems }
		},

		// ============ 错误显示相关方法 ============
		showDetailedErrors(failedItems, title, type = 'error') {
			if (!failedItems || failedItems.length === 0) return
			
			// 统计错误类型和数量
			const errorStats = {}
			const itemDetails = []
			
			failedItems.forEach((item, index) => {
				const errorMsg = item.errorMessage || 'Unknown error'
				
				// 统计错误类型
				if (errorStats[errorMsg]) {
					errorStats[errorMsg]++
				} else {
					errorStats[errorMsg] = 1
				}
				
				// 记录具体项目信息 (只显示前10项避免过长)
				if (index < 10) {
					const tracking = item.tracking || 'N/A'
					const upc = item.upc || 'N/A'
					const errorMsg = item.errorMessage || 'Unknown error'
					itemDetails.push(`${index + 1}. Tracking: ${tracking} | UPC: ${upc}<br>&nbsp;&nbsp;&nbsp;&nbsp;<span style="color: #E6A23C;">Error: ${errorMsg}</span>`)
				}
			})
			
			// 构建显示内容
			let content = `<div style="text-align: left; max-height: 400px; overflow-y: auto;">`
			
			// 错误摘要
			content += `<h4>Error Summary:</h4><ul>`
			Object.keys(errorStats).forEach(error => {
				content += `<li><strong>${error}</strong> (${errorStats[error]} items)</li>`
			})
			content += `</ul>`
			
			// 失败项目列表（最多显示10项）
			if (itemDetails.length > 0) {
				content += `<h4>Failed Items Details ${failedItems.length > 10 ? '(showing first 10)' : ''}:</h4>`
				content += `<div style="font-family: monospace; font-size: 12px; background: #f5f5f5; padding: 10px; border-radius: 4px;">`
				content += itemDetails.join('<br>')
				if (failedItems.length > 10) {
					content += `<br>... and ${failedItems.length - 10} more items`
				}
				content += `</div>`
			}
			
			content += `</div>`
			
			// 使用MessageBox显示，支持HTML内容
			this.$msgbox({
				title: title,
				message: content,
				type: type,
				dangerouslyUseHTMLString: true,
				showClose: true,
				closeOnClickModal: true,
				customClass: 'error-details-dialog',
				confirmButtonText: 'OK'
			}).catch(() => {
				// 用户关闭对话框，不需要处理
			})
		},

		// ============ 页面滚动相关方法 ============
		handleScroll() {
			// 当页面滚动距离超过 300px 时显示按钮
			// 找到实际的scrollable容器：.content-container (在Home.vue中定义)
			const contentContainer = document.querySelector('.content-container')
			let scrollY = 0

			if (contentContainer) {
				// 从.content-container获取滚动距离
				scrollY = contentContainer.scrollTop
			} else {
				// 备用方案：使用window滚动距离
				scrollY = window.pageYOffset || document.documentElement.scrollTop
			}

			this.showScrollToTopButton = scrollY > 300
		},

		handleScrollToTopClick() {
			// 点击事件处理函数 - 回到顶部
			console.log('回到顶部按钮被点击')

			// 找到实际的scrollable容器：.content-container (在Home.vue中定义，overflow-y: scroll)
			const contentContainer = document.querySelector('.content-container')

			if (contentContainer) {
				// 直接设置scrollTop到0
				contentContainer.scrollTop = 0
				console.log('已将 .content-container 滚动到顶部')
			} else {
				// 备用方案：使用window滚动
				window.scrollTo({
					top: 0,
					behavior: 'smooth'
				})
				console.log('使用window.scrollTo滚动到顶部')
			}

			// 立即隐藏按钮
			this.showScrollToTopButton = false
		}
	},

	watch: {
		activeName(newVal) {
			if (newVal === 'myClaims') {
				this.getMyClaimList()
			}
		}
	}
}
</script>

<style scoped>
.note {
	color: #909399;
	font-size: 12px;
	margin: 2px 0;
}

/* 只读输入框样式 */
.readonly-input {
	background-color: #f5f7fa !important;
}

.readonly-input .el-input__inner {
	background-color: #f5f7fa !important;
	color: #606266 !important;
	cursor: not-allowed !important;
	border-color: #dcdfe6 !important;
}

/* 只读选择框样式 */
.readonly-select .el-input__inner {
	background-color: #f5f7fa !important;
	color: #606266 !important;
	cursor: not-allowed !important;
	border-color: #dcdfe6 !important;
}

.readonly-select .el-input__suffix {
	color: #c0c4cc !important;
}

/* 只读状态提示文字 */
.readonly-hint {
	font-size: 12px;
	color: #909399;
	margin-top: 4px;
	display: flex;
	align-items: center;
}

.readonly-hint .el-icon-info {
	margin-right: 4px;
	color: #409EFF;
}

/* 表单行间距 */
.form-row-spaced {
	margin-bottom: 30px !important;
}

.toolbar {
	background: #f2f6fc;
	padding: 10px;
	margin: 10px 0;
}

.action-buttons {
	display: flex;
	flex-wrap: nowrap;
	gap: 8px;
	align-items: center;
	justify-content: center;
}

.action-buttons .el-button {
	margin: 0 !important;
	flex-shrink: 0;
	min-width: 60px !important;
	height: 28px !important;
	padding: 7px 15px !important;
	font-size: 12px !important;
	line-height: 1 !important;
	box-sizing: border-box;
}

.action-buttons .el-button--mini {
	height: 28px !important;
	padding: 7px 15px !important;
	min-width: 60px !important;
}

.action-buttons .el-button--danger {
	height: 28px !important;
	padding: 7px 15px !important;
	min-width: 60px !important;
	text-align: center !important;
	display: flex !important;
	align-items: center !important;
	justify-content: center !important;
}

.action-btn {
	display: inline-block !important;
	width: 60px !important;
	height: 28px !important;
	padding: 7px 15px !important;
	margin: 0 !important;
	font-size: 12px !important;
	line-height: 12px !important;
	text-align: center !important;
	border-radius: 3px !important;
	box-sizing: border-box !important;
	vertical-align: middle !important;
}

/* 错误详情对话框样式 */
.error-details-dialog .el-message-box {
	width: 600px !important;
	max-width: 90vw !important;
}

.error-details-dialog .el-message-box__content {
	max-height: 500px !important;
	overflow-y: auto !important;
}

.error-details-dialog h4 {
	margin: 15px 0 8px 0 !important;
	color: #303133 !important;
	font-size: 14px !important;
	font-weight: 600 !important;
}

.error-details-dialog ul {
	margin: 8px 0 !important;
	padding-left: 20px !important;
}

.error-details-dialog li {
	margin: 5px 0 !important;
	line-height: 1.4 !important;
}

.error-details-dialog .el-message-box__message {
	padding: 10px 15px !important;
}

/* 回到顶部浮动按钮样式 */
.scroll-to-top-button {
	position: fixed !important;
	bottom: 30px !important;
	right: 30px !important;
	z-index: 9999 !important;
	display: block !important;
	visibility: visible !important;
	opacity: 1 !important;
	width: 50px !important;
	height: 50px !important;
	box-shadow: 0 2px 12px rgba(0, 0, 0, 0.15) !important;
}

.scroll-to-top-button:hover {
	transform: scale(1.1) !important;
}

/* Transition动画 */
.fade-enter-active,
.fade-leave-active {
	transition: opacity 0.3s ease;
}

.fade-enter,
.fade-leave-to {
	opacity: 0;
}
</style>


// WEBPACK FOOTER //
// mixedPackageClaim.vue?615a9dd1